import { postfixTestId } from '../../util/testId';
import {
  useState,
  useEffect,
  createElement,
  ReactNode,
  Children,
  useRef,
  CSSProperties,
  useCallback,
} from 'react';
import { TokenizerProps, TokenizerToken } from './types';
import { TokenDesign, GroupToken, TokenDesignProps } from '../Token';
import { KeyInteractionType } from '../Token';
import { Icon } from '../Icon';
import { Scrollable } from '../Scrollable';
import { useRefArray } from '../../hooks/useRefArray';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import useOnFocusOut from '../../hooks/useOnFocusOut';
import { DisclosureNextBreadcrumb } from '@sas/react-svg';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { BusyStateIndicator } from '../BusyStateIndicator';
import { useFormatter } from '../../locale/formatting';
import { isNullish } from '@sas/nova-core/typeguards';
import bundle from './NovaComponents-Tokenizer-gui-icu.properties';
import cx from 'classnames';
import cn from './Tokenizer.css.json';
import './Tokenizer.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const Tokenizer = (props: TokenizerProps) => {
  const localize = useTextLocalizer();
  const formatter = useFormatter();
  const [focusedIndex, setFocusedIndex] = useState<number>();
  const lastFocusedIndex = useRef(0);
  const [scrollIndex, setScrollIndex] = useState<number>(0);
  const [focusedIndexChangedCount, setFocusedIndexChangedCount] = useState(0);
  const [isTokenRemoved, setIsTokenRemoved] = useState(false);
  const { createSetRef: createTokenRef } = useRefArray<HTMLDivElement>();
  const containerRef = useRef<HTMLDivElement>(null);
  const prevTokens = usePreviousValue(props.tokens);
  const refChild = useRef<HTMLDivElement>(null);
  const keyInteractionRef = useRef<KeyInteractionType>(undefined);

  const setFocus = useCallback((index: number) => {
    setFocusedIndex(index);
    lastFocusedIndex.current = index;
  }, []);

  // Update scrollIndex
  useEffect(() => {
    if (focusedIndex !== undefined) {
      setScrollIndex(focusedIndex);
    }
  }, [focusedIndex]);

  const [tokenMaxWidth, setTokenMaxWidth] = useState<number>();
  const [setScrollableContentRef, getScrollableContent] = useResizeObserverRef<HTMLDivElement>(
    () => {
      const scrollableContent = getScrollableContent();
      if (!scrollableContent) return;
      setTokenMaxWidth(scrollableContent.clientWidth);
    }
  );

  let tokens: ReactNode[];
  let tokenFocusBlurHandler: TokenDesignProps['onTokenFocusBlur'];

  if (props.groupEnabled) {
    const groupedTokens: {
      [groupId: string]: { token: TokenizerToken; originalIndex: number }[];
    } = {};

    const groupingThreshold =
      props.groupingThreshold && props.groupingThreshold > 1 ? props.groupingThreshold : 20;
    const aTokenCount: { [groupId: string]: { groupTokenCount: number } } = {};
    const aTokens: (string | { token: TokenizerToken; originalIndex: number })[] = [];
    // Group tokens based on groupId
    // Note: tokens without groupId or groupId being empty string are grouped together

    // count the token count for each group id
    for (const token of props.tokens) {
      const { groupId = '' } = token;
      if (!aTokenCount[groupId]) {
        aTokenCount[groupId] = { groupTokenCount: 1 };
      } else {
        aTokenCount[groupId].groupTokenCount++;
      }
    }

    props.tokens.forEach((token, i) => {
      const { groupId = '' } = token;
      const iTokenCount = aTokenCount[groupId].groupTokenCount;

      if (iTokenCount >= groupingThreshold) {
        if (!groupedTokens[groupId]) {
          groupedTokens[groupId] = [];
        }

        groupedTokens[groupId].push({ token, originalIndex: i });
        if (!aTokens.includes(groupId)) {
          aTokens.push(groupId);
        }
      } else {
        aTokens.push({ token, originalIndex: i });
      }
    });

    const onRemoveTokens = (groupIdIndex: number, tokenIndex?: number) => {
      if (!props.onRemoveTokens) {
        return;
      }
      const indices: number[] = [];
      let startIndex = groupIdIndex;
      const endIndex = props.hasBreadcrumbs ? aTokens.length : groupIdIndex + 1;

      if (tokenIndex !== undefined) {
        const tokenObj = aTokens[groupIdIndex];
        if (typeof tokenObj === 'string') {
          const groupTokens = groupedTokens[tokenObj]; // tokenObj is the groupId
          indices.push(groupTokens[tokenIndex].originalIndex);
        } else {
          indices.push(tokenObj.originalIndex);
        }
        startIndex = groupIdIndex + 1;
      }

      for (let i = startIndex; i < endIndex; i++) {
        const token = aTokens[i];
        if (typeof token === 'object') {
          indices.push(token.originalIndex);
        } else {
          // token is the groupId
          for (const { originalIndex } of groupedTokens[token]) {
            indices.push(originalIndex);
          }
        }
      }

      if (indices.length === props.tokens.length) {
        // onBlur wouldn't be triggered to reset the focusIndex
        // when the last token is removed from the DOM, so manually reset it here
        setFocusedIndex(undefined);
      }
      props.onRemoveTokens(indices);
    };

    tokens = aTokens.map((token, i) => {
      tokenFocusBlurHandler = (focusInteraction: KeyInteractionType) => {
        switch (focusInteraction) {
          case 'right':
            keyInteractionRef.current = 'right';
            if (i !== aTokens.length - 1) {
              setFocus(i + 1);
            }
            break;
          case 'left':
            keyInteractionRef.current = 'left';
            if (i !== 0) {
              setFocus(i - 1);
            }
            break;
          case 'ctrlHome':
            keyInteractionRef.current = 'ctrlHome';
            setFocus(0);
            break;
          case 'ctrlEnd':
            keyInteractionRef.current = 'ctrlEnd';
            setFocus(aTokens.length - 1);
            break;
        }
      };

      let groupId: string;
      let groupTokens: { token: TokenizerToken; originalIndex: number }[] = [];
      if (typeof token === 'object') {
        groupId = token.token.groupId!;
        groupTokens.push(token);
      } else {
        groupId = token;
        groupTokens = groupedTokens[groupId];
      }

      const className = cn.token;
      const onFocus = () => {
        keyInteractionRef.current = undefined;
        setFocus(i);
      };
      const tabIndex = lastFocusedIndex.current === i ? 0 : -1;
      // Only append aria info when not focused or being focused for the first time
      // so that those info is only annouced once when nagivating within tokens
      const shouldAppendAriaInfo = focusedIndexChangedCount < 2 && lastFocusedIndex.current === i;
      const ariaLabels = shouldAppendAriaInfo && props.ariaLabel ? [props.ariaLabel] : [];

      const iTokenCount = aTokenCount[groupId].groupTokenCount;
      if (iTokenCount < groupingThreshold) {
        const token = groupTokens[0].token;
        ariaLabels.push(token.ariaLabel ?? token.value);
        const tokenNode = (
          <TokenDesign
            key={groupId}
            testId={postfixTestId(props.testId, 'token' + `${i}`)}
            ref={createTokenRef(i)}
            className={className}
            tabIndex={tabIndex}
            onFocus={onFocus}
            onClose={() => {
              props.onCloseToken?.(token);
              onRemoveTokens(i);

              keyInteractionRef.current = 'right';
              setIsTokenRemoved(true);
            }}
            value={token.value}
            icon={token.icon}
            tooltip={token.tooltip}
            menuSections={token.menuSections}
            includeDropdown={!!token.menuSections}
            onItemSelected={token.onItemSelected}
            ariaLabel={ariaLabels.join(', ')}
            ariaDescribedBy={shouldAppendAriaInfo ? props.ariaDescribedBy : undefined}
            readOnly={token.readOnly}
            onTokenFocusBlur={tokenFocusBlurHandler}
            keyInteraction={keyInteractionRef.current}
            hasTokenCountChanged={isTokenRemoved}
            isFirstToken={i === 0}
            isLastToken={i === aTokens.length - 1}
            variant={props.variant}
            id={token.id}
            dndPrefix={token.dndPrefix}
            allowDrag={token.allowDrag}
            closeButtonProps={token.closeButtonProps}
            index={i}
          />
        );
        return token.busyStateIndicatorProps ? (
          <BusyStateIndicator
            delay={0}
            {...token.busyStateIndicatorProps}
            key={groupId}
            containerClassName={cx(
              cn.busyStateContainer,
              token.busyStateIndicatorProps.containerClassName
            )}
          >
            {tokenNode}
          </BusyStateIndicator>
        ) : (
          tokenNode
        );
      } else {
        const label = props.groupLabelMap?.[groupId] ?? groupId;

        const formattedCount = formatter?.formatNumeric(groupTokens.length) ?? groupTokens.length;
        ariaLabels.push(`${label} (${formattedCount})`);
        return (
          <GroupToken
            key={groupId}
            testId={postfixTestId(props.testId, label)}
            ref={createTokenRef(i)}
            className={className}
            tabIndex={tabIndex}
            onFocus={onFocus}
            onClose={() => {
              props.onCloseGroup?.(groupId);
              onRemoveTokens(i);

              keyInteractionRef.current = 'right';
              setIsTokenRemoved(true);
            }}
            onCloseItem={(index) => {
              props.onCloseToken?.(groupTokens[index].token);
              onRemoveTokens(i, index);

              if (iTokenCount <= groupingThreshold) {
                keyInteractionRef.current = 'right';
                setIsTokenRemoved(true);
              }
            }}
            label={label}
            ariaLabel={ariaLabels.join(', ')}
            ariaDescribedBy={shouldAppendAriaInfo ? props.ariaDescribedBy : undefined}
            values={groupTokens.map(({ token: { value } }) => value)}
            // FIXME: GroupToken should be readOnly only if all of the tokens are readOnly, but
            //      | GroupToken does not support items with different readOnly values.
            readOnly={groupTokens.some(({ token: { readOnly } }) => readOnly)}
            onTokenFocusBlur={tokenFocusBlurHandler}
            keyInteraction={keyInteractionRef.current}
            hasTokenCountChanged={isTokenRemoved}
            isFirstToken={i === 0}
            isLastToken={i === aTokens.length - 1}
            variant={props.variant}
            showMoreThreshold={props.groupTokenShowMoreThreshold}
            onMorePress={() => {
              props.onMorePress?.(groupId);
            }}
          />
        );
      }
    });
  } else {
    tokens = props.tokens.map((token, i, tokenItems) => {
      // Only append aria info when not focused or being focused for the first time
      // so that those info is only annouced once when nagivating within tokens
      const shouldAppendAriaInfo = focusedIndexChangedCount < 2 && lastFocusedIndex.current === i;
      tokenFocusBlurHandler = (focusInteraction: KeyInteractionType) => {
        switch (focusInteraction) {
          case 'right':
            keyInteractionRef.current = 'right';
            if (i !== tokenItems.length - 1) {
              setFocus(i + 1);
            }
            break;
          case 'left':
            keyInteractionRef.current = 'left';
            if (i !== 0) {
              setFocus(i - 1);
            }
            break;
          case 'ctrlHome':
            keyInteractionRef.current = 'ctrlHome';
            setFocus(0);
            break;
          case 'ctrlEnd':
            keyInteractionRef.current = 'ctrlEnd';
            setFocus(tokenItems.length - 1);
            break;
        }
      };
      const tokenNode = (
        <TokenDesign
          key={i}
          ref={createTokenRef(i)}
          testId={postfixTestId(props.testId, token.value)}
          className={cn.token}
          onFocus={() => {
            keyInteractionRef.current = undefined;
            setFocus(i);
          }}
          value={token.value}
          icon={token.icon}
          tooltip={token.tooltip}
          menuSections={token.menuSections}
          includeDropdown={!!token.menuSections}
          onItemSelected={token.onItemSelected}
          ariaLabel={
            shouldAppendAriaInfo && props.ariaLabel
              ? [token.ariaLabel ?? token.value, props.ariaLabel].join(', ')
              : token.ariaLabel
          }
          ariaDescribedBy={shouldAppendAriaInfo ? props.ariaDescribedBy : undefined}
          readOnly={token.readOnly}
          onClose={() => {
            props.onCloseToken?.(token);

            const indices: number[] = [];
            const endIndex = props.hasBreadcrumbs ? props.tokens.length : i + 1;
            for (let ii = i; ii < endIndex; ii++) {
              indices.push(ii);
            }
            if (
              focusedIndex !== undefined &&
              (props.hasBreadcrumbs || i === props.tokens.length - 1)
            ) {
              // Set the focus to previous token if deleting the last token
              // Otherwise the focus would temporarily lost
              setFocus(i - 1);
            }
            if (indices.length === props.tokens.length) {
              setFocusedIndex(undefined);
              lastFocusedIndex.current = 0;
            }
            props.onRemoveTokens?.(indices);

            keyInteractionRef.current = 'right';
            setIsTokenRemoved(true);
          }}
          tabIndex={lastFocusedIndex.current === i ? 0 : -1}
          onTokenFocusBlur={tokenFocusBlurHandler}
          keyInteraction={keyInteractionRef.current}
          hasTokenCountChanged={isTokenRemoved}
          isFirstToken={i === 0}
          isLastToken={i === tokenItems.length - 1}
          variant={props.variant}
          id={token.id}
          dndPrefix={token.dndPrefix}
          allowDrag={token.allowDrag}
          index={i}
          closeButtonProps={token.closeButtonProps}
        />
      );
      return token.busyStateIndicatorProps ? (
        <BusyStateIndicator
          delay={0}
          {...token.busyStateIndicatorProps}
          key={i}
          containerClassName={cx(
            cn.busyStateContainer,
            token.busyStateIndicatorProps.containerClassName
          )}
        >
          {tokenNode}
        </BusyStateIndicator>
      ) : (
        tokenNode
      );
    });
  }

  // scroll to the newly added token
  useEffect(() => {
    if (
      isNullish(focusedIndex) &&
      !isTokenRemoved &&
      tokens.length > 0 &&
      scrollIndex !== tokens.length - 1 &&
      prevTokens?.length &&
      prevTokens?.length !== props.tokens.length
    ) {
      setScrollIndex(tokens.length - 1);
    }
  }, [
    focusedIndex,
    scrollIndex,
    isTokenRemoved,
    prevTokens?.length,
    props.tokens.length,
    tokens.length,
  ]);

  // Resetting isTokenRemoved after a token has been removed
  useEffect(() => {
    if (isTokenRemoved) {
      setIsTokenRemoved(false);
    }
  }, [isTokenRemoved, tokens.length]);

  useEffect(() => {
    if (tokens.length < 1 || focusedIndex === undefined) {
      return;
    }
    const lastIndex = tokens.length - 1;
    if (focusedIndex > lastIndex) {
      setFocus(lastIndex);
    }
  }, [tokens.length, focusedIndex, setFocus]);

  // Scroll to the bottom when new token is added
  useEffect(() => {
    if (!containerRef.current) return;
    if (prevTokens?.length !== undefined && props.tokens.length > prevTokens.length) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [props.tokens, prevTokens]);

  useEffect(() => {
    if (focusedIndex === undefined) {
      setFocusedIndexChangedCount(0);
    } else {
      setFocusedIndexChangedCount((prev) => prev + 1);
    }
  }, [focusedIndex]);

  const { onBlur, onFocus } = useOnFocusOut(() => {
    keyInteractionRef.current = undefined;
    setFocusedIndex(undefined);
  });

  return (
    <div
      role={props.role === '' ? undefined : props.role ?? 'grid'}
      aria-label={
        // Do not set aria-label if there is no role set
        props.role === '' ? undefined : props.ariaLabel ?? localize('tokenizer.default.aria')
      }
      aria-labelledby={props.ariaLabelledBy}
      ref={containerRef}
      onBlur={onBlur}
      onFocus={onFocus}
      data-testid={props.testId}
      className={cx(cn.container, props.wrap && cn.wrap, props.className)}
      style={
        {
          ...props.style,
          // token max-width is determined this way because the CSS min has odd behavior when the tokens are in Scrollable.
          // When tokenMaxWidth is undefined, the scss file calculates a default using min().  tokenMaxWidth should be undefined
          // whenever props.wrap is falsy.
          '--token-max-width': tokenMaxWidth && `${Math.min(remToPx(25), tokenMaxWidth)}px`,
        } as CSSProperties
      }
      tabIndex={-1} // Firefox considers this div focusable when there is a vertical scrollbar, so explicitly set the tabIndex to -1
    >
      {props.wrap ? (
        tokens
      ) : (
        <Scrollable
          contentProps={{ role: 'presentation' }}
          currentIndex={scrollIndex}
          setCurrentIndex={setScrollIndex}
          testId={postfixTestId(props.testId, 'Scroll')}
          separator={
            props.hasBreadcrumbs ? (
              <Icon icon={DisclosureNextBreadcrumb} className={cn.separator} />
            ) : null
          }
          contentRef={setScrollableContentRef}
        >
          {tokens}
        </Scrollable>
      )}
      {Children.count(props.children) > 0 && (
        <div
          className={cn.childrenContainer}
          ref={refChild}
          onFocus={() => setFocusedIndex(undefined)}
          role="row"
        >
          {props.children}
        </div>
      )}
    </div>
  );
};

const remToPx = (rem: number) =>
  rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
